import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Mail, ChevronDown, Send, X as XIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { BACKEND_URL } from '../lib/api';

const PERMISSION_OPTIONS = [
  { key: 'can_scan_tickets', label: 'Scan tickets at the door', default: true },
  { key: 'can_view_guest_list', label: 'View the guest list', default: true },
  { key: 'can_check_in_guests', label: 'Check guests in', default: true },
  { key: 'can_view_basic_event_details', label: 'View basic event details', default: true },
  { key: 'can_resend_tickets', label: 'Resend ticket emails', default: false },
  { key: 'can_view_sales_summary', label: 'View sales summary', default: false },
];

const defaultPermissions = () =>
  PERMISSION_OPTIONS.reduce((acc, p) => ({ ...acc, [p.key]: p.default }), {});

/**
 * Owner-only — invite + list associates. Associates can be invited either to
 * a single event (event_id set, recommended) or org-wide (event_id null).
 * Per-permission checkboxes control what the associate can do.
 */
const TeamSection = ({ events = [] }) => {
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [email, setEmail] = useState('');
  const [eventId, setEventId] = useState('');
  const [permissions, setPermissions] = useState(defaultPermissions());
  const [permsOpen, setPermsOpen] = useState(false);
  const [inviting, setInviting] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/organiser/team`, { withCredentials: true });
      setMembers(res.data?.members || []);
      setPending(res.data?.pending_invitations || []);
    } catch {
      // silent — user might not be an owner
    }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error('Please enter a valid email.');
      return;
    }
    setInviting(true);
    try {
      const payload = {
        email: email.trim(),
        event_id: eventId || null,
        permissions,
      };
      const res = await axios.post(
        `${BACKEND_URL}/api/organiser/team/invite`,
        payload,
        { withCredentials: true },
      );
      if (res.data?.status === 'already_member') {
        toast.info('That person is already on your team.');
      } else {
        toast.success(eventId ? 'Event invitation sent.' : 'Invitation sent.');
      }
      setEmail('');
      setEventId('');
      setPermissions(defaultPermissions());
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send invitation.');
    } finally {
      setInviting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this team member? They will lose access immediately.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/organiser/team/${id}`, { withCredentials: true });
      toast.success('Team member removed.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove.');
    }
  };

  const [resendingId, setResendingId] = useState(null);
  const resendInvite = async (invitationId) => {
    setResendingId(invitationId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/organiser/team/invite/${invitationId}/resend`,
        {},
        { withCredentials: true },
      );
      toast.success('Invitation resent successfully.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend invitation.');
    } finally {
      setResendingId(null);
    }
  };

  const [cancellingId, setCancellingId] = useState(null);
  const cancelInvite = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation? The invite link will no longer work.')) return;
    setCancellingId(invitationId);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/organiser/team/invite/${invitationId}`,
        { withCredentials: true },
      );
      toast.success('Invitation cancelled successfully.');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel invitation.');
    } finally {
      setCancellingId(null);
    }
  };

  const togglePerm = (key) => {
    setPermissions((p) => ({ ...p, [key]: !p[key] }));
  };

  return (
    <div data-testid="team-section" className="bg-white rounded-xl shadow-sm border border-[#E5E9E6] mt-8">
      <div className="p-6 border-b border-[#E5E9E6] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-[#1F3D2B]" />
          <h2 className="text-2xl font-semibold text-[#112318]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Team
          </h2>
        </div>
        <span className="text-xs uppercase tracking-widest text-[#4A5D50]">
          {members.length} associate{members.length !== 1 && 's'}
        </span>
      </div>

      <div className="p-6 space-y-5">
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              placeholder="associate@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="team-invite-email"
              className="flex-1"
              required
            />
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="team-invite-event"
            >
              <option value="">All events (org-wide)</option>
              {events.map((ev) => (
                <option key={ev.event_id} value={ev.event_id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </div>

          <div className="border border-stone-200 rounded-lg">
            <button
              type="button"
              onClick={() => setPermsOpen((o) => !o)}
              className="w-full px-4 py-2 flex items-center justify-between text-left text-sm text-[#1F3D2B] hover:bg-stone-50"
              data-testid="team-invite-permissions-toggle"
            >
              <span>Permissions ({Object.values(permissions).filter(Boolean).length} of {PERMISSION_OPTIONS.length})</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${permsOpen ? 'rotate-180' : ''}`} />
            </button>
            {permsOpen && (
              <div className="border-t border-stone-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="team-invite-permissions-list">
                {PERMISSION_OPTIONS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm text-[#4A5D50] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!permissions[p.key]}
                      onChange={() => togglePerm(p.key)}
                      data-testid={`team-invite-perm-${p.key}`}
                      className="rounded"
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={inviting}
              data-testid="team-invite-submit"
              className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-6"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {inviting ? 'Sending…' : 'Send invitation'}
            </Button>
          </div>
        </form>
        <p className="text-xs text-[#4A5D50] -mt-2">
          Associates can scan tickets and manage the events you grant them. Banking and payouts stay with you.
        </p>

        {pending.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wider text-[#4A5D50] mb-2">Pending invitations</p>
            <div className="space-y-2">
              {pending.map((inv) => (
                <div key={inv.invitation_id} data-testid={`team-invite-pending-${inv.invitation_id.slice(0,8)}`} className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Mail className="w-4 h-4 text-amber-700 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-amber-900 truncate">{inv.invitee_email}</p>
                    {inv.event_title && (
                      <p className="text-xs text-amber-800 truncate">For: {inv.event_title}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => resendInvite(inv.invitation_id)}
                    disabled={resendingId === inv.invitation_id || cancellingId === inv.invitation_id}
                    className="rounded-full border-amber-300 text-amber-900 hover:bg-amber-100 h-8 px-3"
                    data-testid={`team-invite-resend-${inv.invitation_id.slice(0,8)}`}
                    title="Resend invitation email"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    {resendingId === inv.invitation_id ? 'Sending…' : 'Resend'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => cancelInvite(inv.invitation_id)}
                    disabled={cancellingId === inv.invitation_id || resendingId === inv.invitation_id}
                    className="rounded-full border-rose-300 text-rose-800 hover:bg-rose-50 h-8 px-3"
                    data-testid={`team-invite-cancel-${inv.invitation_id.slice(0,8)}`}
                    title="Cancel invitation"
                  >
                    <XIcon className="w-3.5 h-3.5 mr-1.5" />
                    {cancellingId === inv.invitation_id ? 'Cancelling…' : 'Cancel'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="text-xs uppercase tracking-wider text-[#4A5D50] mb-2">Active associates</p>
          {members.length === 0 ? (
            <p className="text-sm text-stone-500 italic">No associates yet — invite someone above to help manage events.</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {members.map((m) => (
                <div key={`${m.member_user_id}-${m.event_id || 'org'}`} data-testid={`team-member-${m.member_user_id.slice(0,8)}`} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#112318]">{m.member_email}</p>
                    <p className="text-xs text-stone-500">
                      {m.event_title ? `Event: ${m.event_title}` : 'All events'}
                      {' · '}Joined {new Date(m.joined_at).toLocaleDateString('en-ZA')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(m.member_user_id)}
                    data-testid={`team-remove-${m.member_user_id.slice(0,8)}`}
                    className="text-rose-700 hover:bg-rose-50 rounded-full p-2"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSection;
